import { calculateProgressBarStyles } from '@pages/Library/utils/progressBar';
import classNames from 'classnames';
import { forwardRef, useEffect, useRef } from 'react';
import css from './LoadingStatusPill.module.scss';

export interface LoadingStatusPillProps {
  progress?: number; // 0-100, optional progress percentage
  children: React.ReactNode;
  className?: string;
  padding?: number;
}

export const LoadingStatusPill = forwardRef<HTMLDivElement, LoadingStatusPillProps>(
  ({ progress, children, className, padding = 16 }, ref) => {
    const progressBarRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Update progress bar width and fade-start
    useEffect(() => {
      if (!progressBarRef.current || !containerRef.current || progress === undefined) return;

      const updateProgressBar = () => {
        if (!progressBarRef.current || !containerRef.current) return;

        const currentProgress = progress ?? 0;
        const container = containerRef.current;
        const containerWidth = container.offsetWidth;

        const { visualProgress, fadeStartPercent } = calculateProgressBarStyles(currentProgress);

        // Calculate actual width as percentage of full container (in pixels)
        const actualWidth = (visualProgress / 100) * containerWidth;

        // Set CSS variables for positioning and sizing
        progressBarRef.current.style.setProperty('--fade-start', `${fadeStartPercent}%`);
        progressBarRef.current.style.width = `${actualWidth}px`;
      };

      updateProgressBar();

      // Recalculate on window resize
      window.addEventListener('resize', updateProgressBar);
      return () => window.removeEventListener('resize', updateProgressBar);
    }, [progress]);

    return (
      <div ref={containerRef} className={css.loadingStatusPill}>
        <div ref={ref} className={classNames(css.pillContainer, className)}>
          {progress !== undefined && <div ref={progressBarRef} className={css.progressBar} />}
          <div className={css.pillContents} style={{ paddingLeft: padding, paddingRight: padding }}>
            <span className={css.pillText}>{children}</span>
          </div>
        </div>
      </div>
    );
  },
);

LoadingStatusPill.displayName = 'LoadingStatusPill';
